// @flow
import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { CSVLink } from 'react-csv';
import debounce from 'lodash.debounce';
import {
  Card,
  Table,
  Row,
  Col,
  Icon,
  Tooltip,
  Badge,
} from 'antd';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';

import Loading from 'components/Loading';
import Filters from 'components/Filters';
import formatNumber from 'utils/formatNumber';
import getFilterOperators from 'utils/getFilterOperators';

import {
  PAGES,
  FILTERS_TYPES,
} from 'appconstants';
import {
  SpButton,
  FilterWrapper,
  SpText,
} from 'components/DesignKit';

import { productActions, filterParamsActions } from 'store/actions';
import { FILTERS_SPECS } from 'store/actionTypes';
import moment from 'moment-timezone';

import FilterIcon from 'components/NavIcons/filterIcon';
import ExportIcon from 'components/NavIcons/exportIcon';
import DeleteProduct from './components/DeleteProduct';
import AddProduct from './components/AddProduct';
import EditProduct from './components/EditProduct';

const MOMENT_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const PRODUCTS_FILTERS_TYPES = FILTERS_TYPES.PRODUCT;

type Props = {
  fetchProducts: Function,
  products: Array<Object>,
  loading: boolean,
  test: boolean,
  totalElements: number,
  history: {
    push: Function,
  },
  selectedAccount: Object,
  submitting: boolean,
  addProduct: Function,
  validator: boolean,
  updateProduct: Function,
  deleteProduct: Function,
  setFilterData: Function,
  preserveFilters: Object,
};

const Product = (props: Props) => {
  const {
    loading,
    fetchProducts,
    products,
    totalElements,
    test,
    history,
    selectedAccount,
    submitting,
    addProduct,
    validator,
    updateProduct,
    deleteProduct,
    setFilterData,
    preserveFilters,
  } = props;

  const location = useLocation();
  const myRef = useRef();
  const { pathname } = location;
  const [open, setOpen] = useState(false);
  const [pagination, setPagination] = useState({});
  const [deleteProductModal, setDeleteProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState({});
  const [addProductModal, setAddProductModal] = useState(false);
  const [editProductModal, setEditProductModal] = useState(false);
  const [filterTypes, setFilterTypes] = useState(PRODUCTS_FILTERS_TYPES);
  const [fiterOperators, setFilterOperators] = useState([]);
  const [filterProducts, setFilterProducts] = useState((preserveFilters && preserveFilters.filterProducts)
    ? (preserveFilters && preserveFilters.filterProducts)
    : [{
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      filterValueFrom: '',
      filterValueTo: '',
      checked: true,
    }]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [count, setCount] = useState(null);
  const [fillColor, setFillColor] = useState(false);
  const [filterColor, setFilterColor] = useState(false);
  const currencyType = selectedAccount && selectedAccount.currency;
  const supportedCurrency = (selectedAccount && selectedAccount.supportedCurrencies);
  const preserveParam = ((preserveFilters && preserveFilters.pathname) === pathname)
    ? queryString.parse(preserveFilters.search)
    : setFilterData({
      pathname,
      search: '',
      filterProducts: [{
        filterType: '',
        filterName: '',
        filterOperator: '',
        filterValue: '',
        filterNameDisplay: '',
        filterValueFrom: '',
        filterValueTo: '',
        checked: true,
      }],
    });

  const exportData = products ? products.map((values) => ({
    amount: `${values.currency.prefix}${formatNumber((values.amount / 100).toFixed(values.currency.toFixed), currencyType)}`,
    name: values.name,
    description: values.description,
    createdAt: moment
      .utc(values.createdAt)
      .tz(selectedAccount.timezone)
      .format('MMM DD, YYYY, hh:mm a'),
  })) : '';

  const exportHeaders = [
    { label: 'AMOUNT', key: 'amount' },
    { label: 'NAME', key: 'name' },
    { label: 'DESCRIPTION', key: 'description' },
    { label: 'CREATED ON', key: 'createdAt' },
  ];

  const debouncedFetchProducts = debounce(fetchProducts, 2000, { leading: true });

  const getData = (query) => {
    const parsed = queryString.parse(query);
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    const filterParam = parsed.filterParams || '';
    const filterParams = (preserveParam ? preserveParam.filterParams : filterParam || '');
    debouncedFetchProducts({ currentPage, sortParams, filterParams });
  };

  const handleClick = e => {
    if (myRef.current && myRef.current.contains(e.target)) {
      return;
    }
    if (e.target.value === undefined
      && e.toElement.className !== 'ant-calendar-date'
      && e.toElement.className !== 'ant-calendar-prev-month-btn'
      && e.toElement.className !== 'ant-calendar-next-month-btn') {
      setOpen(false);
    }
  };

  useEffect(() => {
    getData(location.search);
  }, [test, selectedAccount, location]);

  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    setPagination(existingElements => ({
      ...existingElements,
      total: totalElements,
      current: parseInt(currentPage, 10),
      showTotal: total => `Total ${total} items`,
    }));
  }, [products]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  useEffect(() => {
    if (!validator) {
      setAddProductModal(false);
      setDeleteProductModal(false);
      setEditProductModal(false);
    }
  }, [validator]);

  const selectedRecord = (value) => {
    if (value.payout) {
      const payoutGid = value.payout.gid;
      history.push({
        pathname: `${PAGES.PAYOUTS}/${payoutGid}`,
        state: location.search,
      });
    } else if (value.paymentSession) {
      const paymentGid = value.paymentSession.gid;
      history.push({
        pathname: `${PAGES.PAYMENTS}/${paymentGid}`,
        state: location.search,
      });
    }
  };

  const handleTableChange = (page, sorter, filters) => {
    const parsed = queryString.parse(location.search);
    const filterParams = parsed.filterParams || '';
    const tempPage = page.current;
    let tempParams = '';
    if (filters.order === 'ascend') {
      tempParams = `&sortBy=${filters.field}&direction=ASC`;
    } else if (filters.order === 'descend') {
      tempParams = `&sortBy=${filters.field}&direction=DESC`;
    } else {
      tempParams = '';
    }
    if (location.search && filterParams) {
      const filterParam = encodeURIComponent(filterParams);
      history.push({
        pathname: PAGES.PRODUCTS,
        search: `?filterParams=${filterParam}&pageNum=${tempPage}${tempParams}`,
      });
    } else {
      history.push({
        pathname: PAGES.PRODUCTS,
        search: `?pageNum=${tempPage}${tempParams}`,
      });
    }
  };

  const showDeleteProductModal = (value: Object) => {
    setSelectedProduct(value);
    setDeleteProductModal(true);
  };

  const showEditProductModal = (value: Object) => {
    setSelectedProduct(value);
    setEditProductModal(true);
  };

  const deleteSelectedProduct = (gid: string) => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;

    deleteProduct({
      gid,
      currentPage,
      sortParams,
    });
  };

  const editSelectedProduct = (product: Object) => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    const {
      productName,
      currencyCode,
      description,
      amount,
      notes,
      promotion,
      isIndia,
      gid,
      isRecurring,
      upfront,
    } = product;

    const totalAmount = amount.replace(/[. ,:-]+/g, '');

    let params;
    if (isIndia) {
      params = {
        name: productName,
        currencyCode,
        description,
        amount: Number(totalAmount),
        note: notes,
        paymentMethodType: [
          'CARD',
        ],
        statementDescription: '',
        recurring: isRecurring,
        upfront,
      };
    } else {
      params = {
        name: productName,
        currencyCode,
        description,
        amount: Number(totalAmount),
        note: notes,
        paymentMethodType: [
          'CARD',
        ],
        statementDescription: '',
        promotionalProduct: promotion,
        recurring: isRecurring,
        upfront,
      };
    }
    updateProduct({
      currentPage,
      sortParams,
      gid,
      params,
    });
  };

  const addNewProduct = (product: Object) => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;

    const {
      productName,
      currencyCode,
      description,
      amount,
      notes,
      promotion,
      isIndia,
      isRecurring,
      upfront,
    } = product;

    const totalAmount = amount.replace(/[. ,:-]+/g, '');

    let params;
    if (isIndia) {
      params = {
        name: productName,
        currencyCode,
        description,
        amount: Number(totalAmount),
        note: notes,
        paymentMethodType: [
          'CARD',
        ],
        statementDescription: '',
        recurring: isRecurring,
        upfront,
      };
    } else {
      params = {
        name: productName,
        currencyCode,
        description,
        amount: Number(totalAmount),
        note: notes,
        paymentMethodType: [
          'CARD',
        ],
        statementDescription: '',
        promotionalProduct: promotion,
        recurring: isRecurring,
        upfront,
      };
    }
    addProduct({
      params,
      currentPage,
      sortParams,
    });
  };

  const onCheck = (e, index) => {
    const list = [...filterProducts];
    if (filterProducts.length !== 1) {
      if (!e) {
        list.splice(index, 1);
        const filterTypeOptions = PRODUCTS_FILTERS_TYPES.filter(({ value: id1 }) => !list.some(({ filterName: id2 }) => id2 === id1));
        setFilterTypes(filterTypeOptions);
      } else {
        list[index].checked = e;
      }
    }
    setFilterProducts(list);
  };

  const handleChange = (e, index) => {
    const list = [...filterProducts];
    if (e[1] === 'createdAt') {
      list[index].filterType = 'Date';
    } else if (e[1] === 'amount') {
      list[index].filterType = 'Number';
    } else {
      list[index].filterType = 'String';
    }
    list[index].filterName = e[1];
    list[index].filterNameDisplay = e[0];
    list[index].filterOperator = '';
    list[index].filterValue = '';
    const filterTypeOptions = PRODUCTS_FILTERS_TYPES.filter(({ value: id1 }) => !list.some(({ filterName: id2 }) => id2 === id1));
    setFilterProducts(list);
    setFilterTypes(filterTypeOptions);
    setFilterOperators(getFilterOperators(e[1]));
  };

  const handleFromValue = (e, index) => {
    const list = [...filterProducts];
    const regex = /^\d*\.?\d*$/;
    if (e.currentTarget && regex.test(e.currentTarget.value.replace(/,/g, ''))) {
      list[index].filterValueFrom = e.currentTarget.value;
      setFilterProducts(list);
    } else if (regex.test(e.replace(/,/g, ''))) {
      list[index].filterValueFrom = e;
      setFilterProducts(list);
    }
  };

  const handleToValue = (e, index) => {
    const list = [...filterProducts];
    const regex = /^\d*\.?\d*$/;
    if (e.currentTarget && regex.test(e.currentTarget.value.replace(/,/g, ''))) {
      list[index].filterValueTo = e.currentTarget.value;
      setFilterProducts(list);
    } else if (regex.test(e.replace(/,/g, ''))) {
      list[index].filterValueTo = e;
      setFilterProducts(list);
    }
  };

  const handleValue = (e, index) => {
    let selectedValue = '';
    if (filterProducts[index].filterName === 'createdAt') {
      if (filterProducts[index].filterOperator === 'BT') {
        const date1 = e[0];
        const date2 = e[1];
        const utcDate1 = moment(date1).endOf('day');
        setStartDate(utcDate1);
        const utcDate2 = moment(date2).startOf('day');
        setEndDate(utcDate2);
        const utcString1 = utcDate1.format(MOMENT_FORMAT);
        const utcString2 = utcDate2.format(MOMENT_FORMAT);
        const selectedDate1 = moment.tz(utcString1, selectedAccount.timezone);
        const selectedDate2 = moment.tz(utcString2, selectedAccount.timezone);
        const dateStart = selectedDate1.utc().format(MOMENT_FORMAT);
        const dateEnd = selectedDate2.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.GT=${dateStart}&createdAt.LT=${dateEnd}`;
      } else if (filterProducts[index].filterOperator === 'LT') {
        const utcDate = moment(e).startOf('day');
        setCurrentDate(utcDate);
        const utcString = utcDate.format(MOMENT_FORMAT);
        const dateSelected = moment.tz(utcString, selectedAccount.timezone);
        const dateStart = dateSelected.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.LT=${dateStart}`;
      } else if (filterProducts[index].filterOperator === 'GT') {
        const utcDate = moment(e).endOf('day');
        setCurrentDate(utcDate);
        const utcString = utcDate.format(MOMENT_FORMAT);
        const dateSelected = moment.tz(utcString, selectedAccount.timezone);
        const dateStart = dateSelected.utc().format(MOMENT_FORMAT);
        selectedValue = `createdAt.GT=${dateStart}`;
      }
    } else {
      if (!e.currentTarget) {
        selectedValue = e;
      }
      if (e.currentTarget && e.currentTarget.value) {
        selectedValue = e.currentTarget.value;
      }
    }
    const list = [...filterProducts];
    list[index].filterValue = selectedValue;
    setFilterProducts(list);
  };

  const handleOperator = (e, index) => {
    const list = [...filterProducts];
    list[index].filterOperator = e;
    if (list[index].filterValue) {
      if (list[index].filterOperator !== 'BT'
        && list[index].filterName === 'createdAt') {
        handleValue(currentDate, index);
      } else if (list[index].filterOperator === 'BT'
        && list[index].filterName === 'createdAt') {
        const dateList = [startDate, endDate];
        handleValue(dateList, index);
      } else if (list[index].filterOperator !== 'BT'
        && list[index].filterOperator !== 'LT'
        && list[index].filterOperator !== 'GT') {
        const value = filterProducts[index].filterValue;
        handleValue(value, index);
      }
    }
    setFilterProducts(list);
  };

  const handleAddClick = () => {
    const list = [...filterProducts];
    setFilterProducts([...list, {
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      filterValueFrom: '',
      filterValueTo: '',
      checked: true,
    }]);
  };

  const resetFilters = () => {
    setFilterData({
      pathname,
      search: '',
    });
    setFilterProducts([{
      filterType: '',
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      filterValueFrom: '',
      filterValueTo: '',
      checked: true,
    }]);
    setFilterTypes(PRODUCTS_FILTERS_TYPES);
    setStartDate('');
    setEndDate('');
    setCurrentDate('');
    setCount(null);
    history.push(PAGES.PRODUCTS);
  };

  const applyFilters = () => {
    const productsFilters = filterProducts.filter((item) => item.checked);
    for (let i = 0; i < productsFilters.length; i += 1) {
      if (!productsFilters[i].filterName || !productsFilters[i].filterOperator || !productsFilters[i].filterValue) {
        return;
      }
    }
    if (productsFilters[0].filterValue || (productsFilters[0].filterValueFrom && productsFilters[0].filterValueTo)) {
      setCount(productsFilters && productsFilters.length);
      for (let i = 0; i < productsFilters.length; i += 1) {
        if (productsFilters[i].checked) {
          if (productsFilters[i].filterName === 'createdAt') {
            productsFilters[i] = productsFilters[i].filterValue;
          } else if (productsFilters[i].filterName === 'amount' && productsFilters[i].filterOperator === 'BT') {
            const name = productsFilters[i].filterName;
            const from = productsFilters[i].filterValueFrom.replace(/[. ,:-]+/g, '');
            const to = productsFilters[i].filterValueTo.replace(/[. ,:-]+/g, '');
            productsFilters[i] = `${name}.GT=${from}&${name}.LT=${to}`;
          } else if (productsFilters[i].filterName === 'amount' && productsFilters[i].filterOperator !== 'BT') {
            const value = productsFilters[i].filterValue.replace(/[. ,:-]+/g, '');
            productsFilters[i] = `${productsFilters[i].filterName}.${productsFilters[i].filterOperator}=${value}`;
          } else {
            productsFilters[i] = `${productsFilters[i].filterName}.${productsFilters[i].filterOperator}=${productsFilters[i].filterValue}`;
          }
        }
      }

      const filterParams = encodeURIComponent(productsFilters.join('&'));
      const parsed = queryString.parse(location.search);
      const currentPage = 1;
      const sortBy = parsed.sortBy || 'createdAt';
      const direction = parsed.direction || 'DESC';
      const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
      if (location.search && parsed.sortBy && parsed.pageNum && parsed.direction) {
        setFilterData({
          pathname,
          search: `?filterParams=${filterParams}&pageNum=${currentPage}${sortParams}`,
          filterProducts,
        });
        history.push({
          pathname: PAGES.PRODUCTS,
          search: `?filterParams=${filterParams}&pageNum=${currentPage}${sortParams}`,
        });
      } else {
        setFilterData({
          pathname,
          search: `?filterParams=${filterParams}`,
          filterProducts,
        });
        history.push({
          pathname: PAGES.PRODUCTS,
          search: `?filterParams=${filterParams}`,
        });
      }
      setOpen(false);
    }
  };

  const columns = [
    {
      title: 'AMOUNT',
      dataIndex: 'amount',
      width: '20%',
      align: 'left',
      render: (amount, record) => {
        const total = (amount / 100).toFixed(record.currency.toFixed);
        if (total < 0) {
          return (
            <span>{`(${record.currency.prefix} ${formatNumber(total, currencyType).replace(/-/g, '')})`}</span>
          );
        }
        return (
          <span> {`${record.currency.prefix} ${formatNumber(total, currencyType)}`}</span>
        );
      },
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'NAME',
      dataIndex: 'name',
      width: '20%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'description',
      width: '25%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (description) => (
        <span>
          {description || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: 'CREATED ON',
      dataIndex: 'createdAt',
      width: '20%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: 'descend',
      render: (createdAt) => {
        if (createdAt && selectedAccount && selectedAccount.timezone) {
          const tzDate = moment
            .utc(createdAt)
            .tz(selectedAccount.timezone)
            .format('MMM DD, YYYY, hh:mm a');
          return (<span>{tzDate}</span>);
        }
        return (
          <>&#8211;</>
        );
      },
    },
    {
      title: 'ACTION',
      dataIndex: 'action',
      width: '15%',
      align: 'center',
      render: (text, record) => (
        <span>
          <Tooltip placement="top" title="Delete">
            <Icon
              type="delete"
              style={{ fontSize: '20px', marginRight: '8px', cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); showDeleteProductModal(record); }}
            />
          </Tooltip>
          <Tooltip placement="top" title="Edit">
            <Icon
              type="edit"
              className="ml-3"
              style={{ fontSize: '20px', cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); showEditProductModal(record); }}
            />
          </Tooltip>
        </span>
      ),
    },
  ];

  if (loading || !selectedAccount) {
    return <Loading />;
  }

  return (
    <Card>
      <Row className="my-2">
        <div className="d-flex justify-content-end">
          <Col>
            <SpButton
              type="secondary"
              shape="round"
              ghost
              className="mr-3"
              onClick={() => setAddProductModal(true)}
            >
              {'\u002B'}&nbsp;Add Product
            </SpButton>
            <span>
              {
                exportData !== null
                  ? (
                    <Tooltip placement="left" title="Click here to export Products">
                      <CSVLink
                        target="_blank"
                        className="mr-3"
                        filename="Products Report.csv"
                        data={exportData}
                        headers={exportHeaders}
                        onMouseEnter={() => setFillColor(true)}
                        onMouseLeave={() => setFillColor(false)}
                      >
                        <ExportIcon
                          fill={fillColor
                            ? '#279dfe'
                            : '#C0C0C0'}
                        />
                      </CSVLink>
                    </Tooltip>
                  ) : null
              }
            </span>
            <span ref={myRef}>
              <span
                onClick={() => setOpen(!open)}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setFilterColor(true)}
                onMouseLeave={() => setFilterColor(false)}
              >
                {open && (
                  <Badge count={count} style={{ backgroundColor: '#0090ff' }}>
                    <FilterIcon
                      fill={filterColor
                        ? '#279dfe'
                        : '#C0C0C0'}
                    />
                  </Badge>
                )}
                {!open && (
                  <Tooltip placement="left" title="Click here to filter Products">
                    <Badge count={count} style={{ backgroundColor: '#0090ff' }}>
                      <FilterIcon
                        fill={filterColor
                          ? '#279dfe'
                          : '#C0C0C0'}
                      />
                    </Badge>
                  </Tooltip>
                )}
              </span>
              {open && (
                <FilterWrapper
                  style={{ marginLeft: '-120px' }}
                >
                  <Filters
                    filtersGrid={filterProducts}
                    filterTypes={filterTypes}
                    handleChange={handleChange}
                    onCheck={onCheck}
                    fiterOperators={fiterOperators}
                    handleOperator={handleOperator}
                    handleValue={handleValue}
                    startDate={startDate}
                    endDate={endDate}
                    currentDate={currentDate}
                    handleFromValue={handleFromValue}
                    handleToValue={handleToValue}
                  />
                  <div className="px-4 mt-2">
                    {
                      filterProducts.length < PRODUCTS_FILTERS_TYPES.length && (
                        <div className="mb-2 ml-3" style={{ cursor: 'pointer' }}>
                          <SpText
                            fontWeight="600"
                            color="#279dfe"
                            onClick={handleAddClick}
                          >
                            +&nbsp; Add another filter
                          </SpText>
                        </div>
                      )
                    }
                  </div>
                  <div className="d-flex mb-2 justify-content-end mr-3">
                    <SpButton
                      type="secondary"
                      shape="round"
                      className="mr-2"
                      ghost
                      onClick={resetFilters}
                    >
                      Clear
                    </SpButton>
                    <SpButton
                      type="primary"
                      shape="round"
                      onClick={applyFilters}
                    >
                      Save
                    </SpButton>
                  </div>
                </FilterWrapper>
              )}
            </span>
          </Col>
        </div>
      </Row>
      {/* DELETE PRODUCT MODAL */}
      {deleteProductModal && (
        <DeleteProduct
          visible={deleteProductModal}
          selectedProduct={selectedProduct}
          submitting={submitting}
          close={() => setDeleteProductModal(false)}
          submit={deleteSelectedProduct}
        />
      )}

      {/* ADD PRODUCT MODAL */}
      {addProductModal && (
        <AddProduct
          visible={addProductModal}
          submitting={submitting}
          selectedAccount={selectedAccount}
          supportedCurrency={supportedCurrency}
          close={() => setAddProductModal(false)}
          submit={addNewProduct}
        />
      )}

      {/* EDIT PRODUCT MODAL */}
      {editProductModal && (
        <EditProduct
          visible={editProductModal}
          submitting={submitting}
          selectedProduct={selectedProduct}
          selectedAccount={selectedAccount}
          supportedCurrency={supportedCurrency}
          submit={editSelectedProduct}
          close={() => setEditProductModal(false)}
        />
      )}

      <Table
        onRow={(record) => ({
          onClick: () => {
            selectedRecord(record);
          },
        })}
        rowClassName={() => 'ant-table-clickable-row'}
        columns={columns}
        rowKey={(record) => record.gid}
        dataSource={products}
        pagination={pagination}
        onChange={handleTableChange}
      />
    </Card>
  );
};

const mapStateToProps = (state) => ({
  products: state.products.content,
  loading: state.loading.loading,
  test: state.account.test,
  totalElements: state.products.totalElements,
  selectedAccount: state.account.selectedAccount,
  submitting: state.loading.submitting,
  validator: state.loading.validator,
  preserveFilters: state.filterParams.preserveFilters,
});

const mapDispatchToProps = (dispatch) => ({
  fetchProducts: param => dispatch(productActions.fetchProducts({
    payload: param,
  })),
  addProduct: param => dispatch(productActions.addProduct({
    payload: param,
  })),
  updateProduct: param => dispatch(productActions.updateProduct({
    payload: param,
  })),
  deleteProduct: param => dispatch(productActions.deleteProduct({
    payload: param,
  })),
  setFilterData: (value) => dispatch(filterParamsActions.setFilterData({
    type: FILTERS_SPECS.SET_FILTER_DATA,
    payload: value,
  })),
});

// $FlowFixMe
export default connect(mapStateToProps, mapDispatchToProps)(Product);
